import React, { useState } from "react";
import TextInput from "../TextInput";
import ReactSelect from "../ReactSelect";
import {
  isPublicOptions,
  orientationOptions,
  resolutionOptions,
  SCREEN_CARTEGORY_OPTIONS,
  SCREEN_SIZE_OPTIONS,
  typeOptions,
} from "./constants";
import { store } from "../../store";
import { AdminServices } from "../../../../../services/admin/AdminServices";
import toast from "react-hot-toast";
import { Switch, FormControlLabel, Box, Typography } from "@mui/material";
import DynamicPricing from "./components/DynamicPricing";

interface FormData {
  screenName: string;
  isPublic: boolean | null;
  merchantAdsQuota: number | null;
  resolution: string;
  orientation: string;
  type: string;
  category: string;
  size: string;
}

type HourlyPrices = {
  [dayNumber: string]: {
    [hour: string]: number;
  };
};

const RegisterScreen: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    screenName: "",
    isPublic: null,
    merchantAdsQuota: null,
    resolution: "",
    orientation: "",
    type: "",
    category: "",
    size: "",
  });

  const [openingHour, setOpeningHour] = useState<number | null>(null);
  const [closingHour, setClosingHour] = useState<number | null>(null);
  const [isDynamicPricing, setIsDynamicPricing] = useState<boolean>(false);
  const [hourlyPrices, setHourlyPrices] = useState<HourlyPrices>({});
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    const state = store.getState();
    const orgId = state.user.selectedOrgId;
    const storeId = state.user.selectedStoreId;
    const userId = state.user.searchedUserDetails?.userId;

    if (!orgId || !storeId) {
      toast.error("Org ID or Store ID not selected");
      return;
    }

    const payload = {
      name: formData.screenName,
      orgId,
      type: "TV",
      userId: userId || "",
      storeId,
      status: "ACTIVE",
      resolution: formData.resolution,
      orientation: formData.orientation,
      merchantAdsQuota: formData.merchantAdsQuota,
      isPublic: formData.isPublic,
      size: formData.size,
      screenCategory: formData.category,
      ...(isDynamicPricing && {
        hourlyPrices,
      }),
    };

    try {
      await AdminServices.registerUserScreen(payload);
      toast.success("Screen Registered successfully");
      resetForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const isDisable =
    formData.screenName.trim() === "" ||
    formData.resolution === "" ||
    formData.orientation === "" ||
    formData.isPublic === null ||
    formData.merchantAdsQuota === null ||
    Number.isNaN(formData.merchantAdsQuota) ||
    formData.type === "" ||
    formData.category === "" ||
    formData.size === "";

  const resetDynamicPricing = () => {
    setOpeningHour(null);
    setClosingHour(null);
    setSelectedDays([]);
    setHourlyPrices({});
  };
  const resetForm = () => {
    setFormData({
      screenName: "",
      isPublic: null,
      merchantAdsQuota: null,
      resolution: "",
      orientation: "",
      type: "",
      category: "",
      size: "",
    });

    setIsDynamicPricing(false);
    resetDynamicPricing();
  };

  return (
    <div className="border rounded-lg shadow-md p-6  mx-auto bg-white">
      <h2 className="text-lg font-bold mb-4">Register Screen</h2>

      <form
        className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full"
        onSubmit={handleRegister}
      >
        <TextInput
          label="Screen Name"
          placeholder="Enter Screen Name"
          value={formData.screenName}
          onChange={(value: string) =>
            setFormData((prev) => ({ ...prev, screenName: value }))
          }
        />

        <ReactSelect
          label="Resolution"
          options={resolutionOptions}
          value={
            resolutionOptions.find((o) => o.value === formData.resolution) ||
            null
          }
          onChange={(option: any) =>
            setFormData((prev) => ({
              ...prev,
              resolution: option?.value || "",
            }))
          }
        />

        <ReactSelect
          label="Orientation"
          options={orientationOptions}
          value={
            orientationOptions.find((o) => o.value === formData.orientation) ||
            null
          }
          onChange={(option: any) =>
            setFormData((prev) => ({
              ...prev,
              orientation: option?.value || "",
            }))
          }
        />

        <ReactSelect
          label="Is Public"
          options={isPublicOptions}
          value={
            isPublicOptions.find((o) => o.value === formData.isPublic) || null
          }
          onChange={(option: any) =>
            setFormData((prev) => ({
              ...prev,
              isPublic: option?.value ?? null,
            }))
          }
        />

        <TextInput
          label="Merchant Ads Quota"
          type="number"
          placeholder="Enter Merchant Ads Quota"
          value={formData.merchantAdsQuota?.toString() || ""}
          onChange={(value: string) =>
            setFormData((prev) => ({
              ...prev,
              merchantAdsQuota: Number(value),
            }))
          }
        />
        <ReactSelect
          label="Select Type"
          options={typeOptions}
          value={typeOptions.find((o) => o.value === formData.type) || null}
          onChange={(option: any) =>
            setFormData((prev) => ({
              ...prev,
              type: option?.value || "",
            }))
          }
          placeholder="Select Type"
        />
        <ReactSelect
          label="Screen Category"
          options={SCREEN_CARTEGORY_OPTIONS}
          value={
            SCREEN_CARTEGORY_OPTIONS.find(
              (o) => o.value === formData.category,
            ) || null
          }
          onChange={(option: any) =>
            setFormData((prev) => ({ ...prev, category: option?.value || "" }))
          }
        />

        <ReactSelect
          label="Screen Size (in inches)"
          options={SCREEN_SIZE_OPTIONS}
          value={
            SCREEN_SIZE_OPTIONS.find((o) => o.value === formData.size) || null
          }
          onChange={(option: any) =>
            setFormData((prev) => ({ ...prev, size: option?.value || "" }))
          }
        />

        {/* 🔹 Dynamic Pricing Toggle */}
        <div className="col-span-full mt-4">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1.5}
            border="1px solid #e5e7eb"
            borderRadius={8}
          >
            <div>
              <Typography fontWeight={600}>Enable Dynamic Pricing</Typography>
              <Typography variant="body2" color="text.secondary">
                Allow different prices based on time slots
              </Typography>
            </div>

            <FormControlLabel
              control={
                <Switch
                  checked={isDynamicPricing}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsDynamicPricing(checked);

                    if (!checked) {
                      resetDynamicPricing();
                    }
                  }}
                  color="primary"
                />
              }
              label=""
            />
          </Box>
        </div>
        {isDynamicPricing && (
          <DynamicPricing
            openingHour={openingHour}
            closingHour={closingHour}
            selectedDays={selectedDays}
            hourlyPrices={hourlyPrices}
            setOpeningHour={setOpeningHour}
            setClosingHour={setClosingHour}
            setSelectedDays={setSelectedDays}
            setHourlyPrices={setHourlyPrices}
          />
        )}

        <div className="col-span-full flex justify-end mt-4">
          <button
            disabled={isDisable}
            type="submit"
            className={`px-6 py-2 rounded-xl text-white ${
              isDisable
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-800 hover:bg-blue-900"
            }`}
          >
            Register
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterScreen;
